import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import AuroraCirculationPumpModeControl from './AuroraCirculationPumpModeControl'
import { AuroraAssetGroup } from '../types'
import { AuroraCirculationPumpMode } from '../enums'
import { useAuroraStack } from '../hooks'
import { setAuroraCirculationPumpMode } from '../services/aurora'
import { createErrorNotification, createSuccessNotification } from '../redux/slices/notifications'

export interface AuroraAssetGroupCirculationPumpModeControlProps {
  auroraAssetGroup: AuroraAssetGroup
  beforeSubmit?: (mode: AuroraCirculationPumpMode) => void
  afterSubmit?: (mode: AuroraCirculationPumpMode) => void
  onError?: (error: unknown) => void
}

export default function AuroraAssetGroupCirculationPumpModeControl({
  auroraAssetGroup,
  beforeSubmit,
  afterSubmit,
  onError
}: AuroraAssetGroupCirculationPumpModeControlProps) {
  const { t } = useTranslation('aurora')

  const dispatch = useDispatch()

  const {
    auroras,
    circulationPumpMode: initialCirculationPumpMode,
    handleAuroraAwhpActionError
  } = useAuroraStack(auroraAssetGroup)

  const [circulationPumpMode, setCirculationPumpMode] = useState<AuroraCirculationPumpMode>(
    initialCirculationPumpMode
  )

  const [isSubmitting, setSubmitting] = useState<boolean>(false)

  async function handleSubmit(value: AuroraCirculationPumpMode) {
    try {
      setSubmitting(true)

      beforeSubmit?.(value)

      await Promise.all(auroras.map((aurora) => setAuroraCirculationPumpMode(aurora.id, value)))

      dispatch(createSuccessNotification(t('setCirculationPumpModeSuccess')))
      setCirculationPumpMode(value)

      afterSubmit?.(value)
    } catch (error) {
      onError?.(error)
      handleAuroraAwhpActionError(error, t('setCirculationPumpModeError'))
    } finally {
      setSubmitting(false)
    }
  }

  if (auroras.length === 0) {
    return null
  }

  return (
    <AuroraCirculationPumpModeControl
      value={circulationPumpMode}
      loading={isSubmitting}
      onSubmit={handleSubmit}
    />
  )
}
